import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LoadingSpinner } from '../../components/loading/LoadingSpinner';
import CreateFolderModal from '../../components/modals/CreateFolderModal.tsx';
import DeleteModal from '../../components/modals/DeleteModal.tsx';
import MoveModal from '../../components/modals/MoveModal.tsx';
import RenameModal from '../../components/modals/RenameModal.tsx';
import ExampleContextMenu from '../../components/projects/ExampleContextMenu.tsx';
import Footer from '../../components/projects/Footer';
import Header from '../../components/projects/Header';
import { NewProjectDialog } from '../../components/projects/NewProjectDialog';
import { NewProjectModal } from '../../components/projects/NewProjectModal';
import ProjectContextMenu from '../../components/projects/ProjectContextMenu.tsx';
import ProjectGrid from '../../components/projects/ProjectGrid';
import Sidebar from '../../components/projects/Sidebar';
import { useDashboardTabNavigation } from '../../components/projects/useDashboardTabNavigation';
import { useAppDispatch, useAppSelector } from '../../hooks.ts';
import { useDrawingsEnabled } from '../../hooks/useDrawingsEnabled';
import { projectsReducer } from '../../store/reducers/projects.reducer.ts';
import { userReducer } from '../../store/reducers/user.reducer.ts';
import {
  getExampleProjectsSelector,
  getFolderSelector,
  getProjectsHasNext,
  getProjectsSelector,
} from '../../store/selectors/projects.selector.ts';
import {
  getIsCloningExampleProject,
  getIsLoadingExampleProjects,
  getIsLoadingFolder,
  getIsLoadingMoreProjects,
  getIsLoadingProjects,
} from '../../store/selectors/ui.selector.ts';
import { getSearchQuery, getUser } from '../../store/selectors/user.selector.ts';
import { Folder } from '../../types/folder.ts';
import { Project } from '../../types/project.ts';

const { loadProjects, loadFolder, loadMoreProjects, loadExampleProjects, cloneExampleProject } =
  projectsReducer.actions;

export const ProjectsPage = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get('folderId') ? Number(searchParams.get('folderId')) : undefined;

  // Backward compatibility: redirect old ?view=catalogue to /catalogue
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam === 'catalogue') {
      navigate('/catalogue', { replace: true });
    }
  }, [searchParams, navigate]);

  const [gridSize, setGridSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [sortBy, setSortBy] = useState<string>('Date created');

  const folder = useAppSelector(getFolderSelector);
  const projects = useAppSelector(getProjectsSelector);
  const exampleProjects = useAppSelector(getExampleProjectsSelector);
  const user = useAppSelector(getUser);
  const searchQuery = useAppSelector(getSearchQuery);
  const isLoadingProjects = useAppSelector(getIsLoadingProjects);
  const isLoadingExampleProjects = useAppSelector(getIsLoadingExampleProjects);
  const isLoadingFolder = useAppSelector(getIsLoadingFolder);
  const isLoadingMore = useAppSelector(getIsLoadingMoreProjects);
  const hasNext = useAppSelector(getProjectsHasNext);
  const isCloningExample = useAppSelector(getIsCloningExampleProject);

  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  // With drawings, a project is made in one step (name and model or drawing) instead of the wizard.
  const drawingsEnabled = useDrawingsEnabled();

  const openCreateFolderModal = () => setIsCreateFolderModalOpen(true);
  const closeCreateFolderModal = () => setIsCreateFolderModalOpen(false);

  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedItemFromContext(null);
  };

  const openMoveModal = () => {
    setIsMoveModalOpen(true);
  };

  const closeMoveModal = () => {
    setIsMoveModalOpen(false);
    setSelectedItemFromContext(null);
  };

  const openRenameModal = () => {
    setIsRenameModalOpen(true);
  };

  const closeRenameModal = () => {
    setIsRenameModalOpen(false);
    setSelectedItemFromContext(null);
  };

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const isAdmin = useMemo(
    () => user?.roles?.includes('ROLE_ADMIN') || user?.roles?.includes('ADMIN') || user?.role === 'ADMIN',
    [user?.role, user?.roles],
  );
  // Analytics is restricted to ADMIN or users with the MANUFACTURER role.
  const canAccessAnalytics = useMemo(
    () => isAdmin || (user?.roles?.includes('MANUFACTURER') ?? false),
    [isAdmin, user?.roles],
  );
  const { updateProjectTemplate } = projectsReducer.actions;

  const handleFolderSelect = (folderId: number | undefined) => {
    const newParams = new URLSearchParams(searchParams);
    if (folderId) {
      newParams.set('folderId', String(folderId));
    } else {
      newParams.delete('folderId');
    }
    setSearchParams(newParams);
  };

  const [contextMenu, setContextMenu] = React.useState<{
    x: number;
    y: number;
    itemId: number;
    itemName: string;
    itemType: 'project' | 'folder' | 'exampleProject';
  } | null>(null);

  const [selectedItemFromContext, setSelectedItemFromContext] = useState<{
    id: number;
    name: string;
    itemType: string;
  } | null>(null);

  const handleOpenContextMenu = (
    e: React.MouseEvent,
    item: Project | Folder,
    itemType: 'project' | 'folder' | 'exampleProject',
  ) => {
    e.preventDefault();

    if (!item || !item.id || !item.name) {
      return;
    }

    setSelectedItemFromContext({
      id: item.id,
      name: item.name,
      itemType: itemType,
    });

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      itemId: item.id,
      itemName: item.name,
      itemType,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode);
  };

  const handleProjectSelect = (projectId: number | null) => {
    setSelectedProjectId(projectId);
  };

  useEffect(() => {
    dispatch(loadFolder(folderId));
    dispatch(loadProjects(folderId));
    // load examples only for root page
    if (!folderId) {
      dispatch(loadExampleProjects());
    }
  }, [dispatch, folderId]);

  const filteredProjects = useMemo(() => {
    const sortedProjects = [...projects].sort((a, b) => {
      switch (sortBy) {
        case 'Name A-Z':
          return a.name.localeCompare(b.name);
        case 'Name Z-A':
          return b.name.localeCompare(a.name);
        case 'Date created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'Date modified':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

    if (!searchQuery) {
      return sortedProjects;
    }

    const query = searchQuery.toLowerCase();
    return sortedProjects.filter((project) => project.name.toLowerCase().includes(query));
  }, [projects, searchQuery, sortBy]);

  const handleTabChange = useDashboardTabNavigation('projects');

  const handleGridSizeChange = (size: 'small' | 'medium' | 'large') => {
    setGridSize(size);
  };

  const handleSortChange = (sort: string) => {
    setSortBy(sort);
  };

  const selectedProject = filteredProjects.find((p) => p.id === selectedProjectId);
  const shouldHideExampleProjects = useMemo(
    () => !!(folderId || user?.settings.hideExampleProjects),
    [folderId, user?.settings.hideExampleProjects],
  );

  const renderDeleteModal = () => {
    if (!isDeleteModalOpen) return null;

    const item =
      selectedItemFromContext || (selectedProject ? { ...selectedProject, itemType: 'project' as const } : null);

    if (!item?.id || !item?.name || !item?.itemType) return null;

    return (
      <DeleteModal
        isOpen={isDeleteModalOpen}
        selectedItemId={item.id}
        selectedItemName={item.name}
        selectedItemType={item.itemType}
        onClose={closeDeleteModal}
      />
    );
  };

  const renderMoveModal = () => {
    if (!isMoveModalOpen) return null;

    const item =
      selectedItemFromContext || (selectedProject ? { ...selectedProject, itemType: 'project' as const } : null);

    if (!item?.id || !item?.name || !item?.itemType || item.itemType !== 'project') return null;

    return (
      <MoveModal
        isOpen={isMoveModalOpen}
        onClose={closeMoveModal}
        selectedItemId={item.id}
        selectedItemName={item.name}
      />
    );
  };

  const renderRenameModal = () => {
    if (!isRenameModalOpen) return null;

    const item =
      selectedItemFromContext || (selectedProject ? { ...selectedProject, itemType: 'project' as const } : null);

    if (!item?.id || !item?.name || !item?.itemType) return null;

    return (
      <RenameModal
        isOpen={isRenameModalOpen}
        selectedItemId={item.id}
        selectedItemName={item.name}
        selectedItemType={item.itemType}
        onClose={closeRenameModal}
      />
    );
  };

  return (
    <div className="family-inter">
      {isCloningExample && <LoadingSpinner fullScreen />}
      <Header
        activeTab="projects"
        onTabChange={handleTabChange}
        showCatalogueTab={true}
        showAnalyticsTab={canAccessAnalytics}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        gridSize={gridSize}
        onGridSizeChange={handleGridSizeChange}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        onCreateProject={() => setIsNewProjectModalOpen(true)}
        onCreateFolder={openCreateFolderModal}
      />

      <div className="flex overflow-hidden" style={{ height: 'calc(100vh - 226px)' }}>
        <Sidebar
          onOpenCreateFolderModal={openCreateFolderModal}
          onOpenDeleteModal={openDeleteModal}
          onOpenMoveModal={openMoveModal}
          onOpenRenameModal={openRenameModal}
          selectedProject={selectedProject}
          onCreateProject={() => setIsNewProjectModalOpen(true)}
        />

        <div className="flex-1 overflow-hidden" style={{ height: 'calc(100vh - 226px)' }}>
          <ProjectGrid
            folders={folder ? [folder] : []}
            isLoading={isLoadingProjects || isLoadingFolder || isLoadingExampleProjects}
            projects={filteredProjects}
            exampleProjects={shouldHideExampleProjects ? [] : exampleProjects}
            viewMode={viewMode}
            onProjectSelect={handleProjectSelect}
            onOpenContextMenu={handleOpenContextMenu}
            onFolderSelect={handleFolderSelect}
            gridSize={gridSize}
            hasNext={hasNext}
            isLoadingMore={isLoadingMore}
            onLoadMore={() => dispatch(loadMoreProjects())}
            onTryExample={(id) => dispatch(cloneExampleProject(id))}
            onOpenRenameModal={(item) => {
              setSelectedItemFromContext({ id: item.id!, name: item.name || '', itemType: 'project' });
              openRenameModal();
            }}
            onOpenMoveModal={(item) => {
              setSelectedItemFromContext({ id: item.id!, name: item.name || '', itemType: 'project' });
              openMoveModal();
            }}
            onOpenDeleteModal={(item) => {
              setSelectedItemFromContext({ id: item.id!, name: item.name || '', itemType: 'project' });
              openDeleteModal();
            }}
            onCreateProject={() => setIsNewProjectModalOpen(true)}
            searchValue={searchQuery}
            // Without the "drawings" feature the search stays in the top bar.
            onSearchChange={
              drawingsEnabled ? (value) => dispatch(userReducer.actions.setSearchQuery(value)) : undefined
            }
            isAdmin={isAdmin}
            onSetProjectTemplate={(id) => dispatch(updateProjectTemplate({ projectId: id, template: true }))}
          />
        </div>
      </div>

      <Footer projectPage={true} />

      {contextMenu && contextMenu.itemType !== 'exampleProject' && (
        <ProjectContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={handleCloseContextMenu}
          itemId={contextMenu.itemId}
          itemType={contextMenu.itemType as 'project' | 'folder'}
          onOpenDeleteModal={openDeleteModal}
          onOpenMoveModal={openMoveModal}
          onOpenRenameModal={openRenameModal}
          isAdmin={isAdmin && contextMenu.itemType === 'project'}
          onSetAsTemplate={() => dispatch(updateProjectTemplate({ projectId: contextMenu.itemId, template: true }))}
        />
      )}

      {contextMenu && contextMenu.itemType === 'exampleProject' && (
        <ExampleContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={handleCloseContextMenu}
          itemId={contextMenu.itemId}
          onTryExample={(id) => dispatch(cloneExampleProject(id))}
          isAdmin={isAdmin}
          onUnsetTemplate={(id) => dispatch(updateProjectTemplate({ projectId: id, template: false }))}
        />
      )}

      <CreateFolderModal folderId={folderId} isOpen={isCreateFolderModalOpen} onClose={closeCreateFolderModal} />

      {renderDeleteModal()}

      {renderMoveModal()}

      {renderRenameModal()}

      {drawingsEnabled ? (
        // Mounted only while open, so each new project starts from an empty dialog.
        isNewProjectModalOpen && <NewProjectDialog open onClose={() => setIsNewProjectModalOpen(false)} />
      ) : (
        <NewProjectModal open={isNewProjectModalOpen} onClose={() => setIsNewProjectModalOpen(false)} />
      )}
    </div>
  );
};
